import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { Button, Menu, Segment } from "semantic-ui-react";
import { toast } from "../..";
import { SetAdditionalOutage } from "../../actions/additionalOutageActions";
import { SetCBBranches } from "../../actions/cbBranchesActions";
import { SetCGMBranches } from "../../actions/cgmBranchesActions";
import { ResetZipFile, ResetXlsxFile, ResetCsvFile } from "../../actions/filesActions";
import { ToggleToastLoading } from "../../actions/toastActions";
import { Configurations } from "../../components/StudyPageTabs/Configurations/Configurations";
import { Logs } from "../../components/StudyPageTabs/Logs/Logs";
import { Validation } from "../../components/StudyPageTabs/Validation/Validation";
import { ICalculation } from "../../models/calculation";
import { createCalculation } from "../../services/calculationsService";
import { AppState } from "../../store/configureStore";
import { SaveLoadCalcButtons } from "./SaveLoadCalcButtons";
import { validateZipFile } from "./validateZipFile";
import { ValidationProcess } from "../../store/validationProcess";
import { Vertices } from "../../components/StudyPageTabs/Configurations/Tabs/Vertices/Vertices";

const menuItemsOptions = ["calculation logs", "configurations"];

interface IProps {
    getCalcJson: (calcType?: number) => ICalculation;
}

export const StudyTabs = ({ getCalcJson }: IProps) => {
    const dispatch = useDispatch();
    const { loading } = useSelector((state: AppState) => state.toast);
    const { selectedTso, selectedTsos } = useSelector((state: AppState) => state.selectedTsos);
    const { zipFile, xlsxFile, csvFile } = useSelector((state: AppState) => state.files);
    const vertices = useSelector((state: AppState) => state.vertices.vertices);
    const vertexSelection = useSelector((state: AppState) => state.vertices.vertexSelection);
    const verticesMode = useSelector((state: AppState) => state.vertices.verticesMode);
    const [activeItem, setActiveItem] = useState(menuItemsOptions[0]);
    const activeTimestamps = useSelector((state: AppState) => state.timestamps.timestamps);
    const validationProcess = useSelector((state: AppState) => state.validationProcess.process);
    const disabledF420upload = !(
        validationProcess === ValidationProcess.IntraDay ||
        validationProcess === ValidationProcess.DayAhead
    );
    const weightDistance = useSelector((state: AppState) => state.ccValidation.weightDistance);

    const handleCreateCalculation = async (calcType: number) => {
        if (!zipFile) toast("Please upload a zip file", false, 30000);
        else if (selectedTsos.length === 0 || selectedTso === "")
            toast("Please select a TSO", false, 30000);
        else if (activeTimestamps.length === 0)
            toast(
                `Please select at least one ${
                    validationProcess === ValidationProcess.Ltcc ? "scenario" : "timestamp"
                }`,
                false,
                30000
            );
        else if (
            (Object.keys(vertices).length < activeTimestamps.length ||
                Object.values(vertices).some(v => !v)) &&
            (vertexSelection === -1 || vertexSelection === undefined) &&
            verticesMode === "manual"
        )
            toast(
                `Please enter vertices data for selected ${
                    validationProcess === ValidationProcess.Ltcc ? "scenarios" : "timestamps"
                }`,
                false,
                30000
            );
        else if (
            verticesMode === "automatic" &&
            (vertexSelection === -1 || vertexSelection === undefined)
        )
            toast(
                "Vertices mode is automatic and vertices selection procedure is empty.\nPlease select the selection procedure before starting calculation.",
                false,
                30000
            );
        else if (vertexSelection === 0 && weightDistance && !csvFile)
            toast("Please upload a CSV file containing weighting factors", false, 30000);
        else {
            dispatch(ToggleToastLoading(true));
            const error = await validateZipFile(zipFile);
            if (error) {
                dispatch(ToggleToastLoading(false));
                toast(error, false, 30000);
                dispatch(ResetZipFile());
                dispatch(ResetCsvFile());
            } else startCalculation(calcType);
        }
    };

    const handleF427Conversion = () => {
        if (!xlsxFile) toast("Please upload a zip file", false, 30000);
        else if (selectedTsos.length === 0 || selectedTso === "")
            toast("Please select a TSO", false, 30000);
        else if (activeTimestamps.length === 0)
            toast("Please select at least one timestamp", false, 30000);
        else {
            dispatch(ToggleToastLoading(true));
            startCalculation(2);
        }
    };

    const startCalculation = async (calcType: number) => {
        const newCalc = getCalcJson(calcType);
        const res = await createCalculation(newCalc, calcType === 2 ? xlsxFile! : zipFile!);
        const message = await res.text();
        if (res.status === 200) {
            toast("Calculation started", true, 30000);
            dispatch(SetAdditionalOutage([]));
            dispatch(SetCGMBranches([]));
            dispatch(SetCBBranches([]));
        }
        else if (typeof message !== "undefined" || message != null) toast(message, false, 30000);
        else toast("Unable to start calculation", false, 30000);
        if (calcType === 2) {
            dispatch(ResetXlsxFile());
        } else {
            dispatch(ResetZipFile());
            dispatch(ResetCsvFile());
        }
        dispatch(ToggleToastLoading(false));
    };

    return (
        <div id="study-tabs-div-a" className="study-tabs">
            <Menu id="menu-studytabs-a" attached="top" tabular>
                {menuItemsOptions.map((name, index) => (
                    <Menu.Item
                        id={`menu-item-studypage-a-${index}`}
                        key={name}
                        name={name}
                        active={activeItem === name}
                        onClick={(e: any, data: any) => setActiveItem(data.name)}
                    />
                ))}
                <Menu.Item id="menu-item-studypage-b" position="right">
                    <SaveLoadCalcButtons getCalcJson={getCalcJson} />
                    <Button
                        id="button-f427-conversion-studypage-a"
                        className="f427-conversion"
                        content="F427 conversion"
                        onClick={() => handleF427Conversion()}
                        disabled={disabledF420upload || loading}
                    />
                    <Button
                        id="button-estimate-security-reserve-studypage-a"
                        className="estimate-security-reserve"
                        content="Estimate security reserve"
                        onClick={() => handleCreateCalculation(1)}
                        disabled={loading}
                    />
                </Menu.Item>
            </Menu>
            <Segment id="segment-studypage" attached="bottom">
                <SwitchTransition mode="out-in">
                    <CSSTransition
                        id="csstransition-studypage"
                        key={activeItem}
                        addEndListener={(node, done) => {
                            node.addEventListener("transitionend", done, false);
                        }}
                        classNames="fade"
                        unmountOnExit
                    >
                        {(() => {
                            switch (activeItem) {
                                case "calculation logs":
                                    return <Logs />;
                                case "configurations":
                                    return <Configurations />;
                                case "validation":
                                    return <Validation />;
                                default:
                                    return null;
                            }
                        })()}
                    </CSSTransition>
                </SwitchTransition>
            </Segment>
            <div id={"df"} style={{ display: 'none' }}>{<Vertices hidden={true} />}</div>
        </div>
    );
};
